// The Decision surface (GDD §3.2, M3): work mode + the Living dial, with
// submit-or-continue semantics and an exact engine-computed preview of the
// rest of the year. "Until submitted, last year's policy continues" — filing
// at an unreviewed year boundary IS the annual review; filing any other time
// is a mid-year adjustment (§4.2), effective immediately.

import { useEffect, useState } from 'react';
import type { Mode, Params } from '@lifespan/engine';
import {
  continuationStudy,
  effectivePlan,
  previewYear,
  projectDeath,
  settledAge,
  skillModel,
  type PlanDraft,
  type Session,
} from '../game/driver.ts';
import { JOB_TITLES } from '../game/jobs.ts';
import { money } from '../game/format.ts';

const MODES: { mode: Mode; label: string; multLabel: string }[] = [
  { mode: 'sab', label: 'Sabbatical', multLabel: '×0.25' },
  { mode: 'std', label: 'Standard', multLabel: '×1.0' },
  { mode: 'ot', label: 'Overtime', multLabel: '×1.6' },
];

export function PlanPanel({
  session,
  P,
  onSubmit,
  currentProjection,
}: {
  session: Session;
  P: Params;
  onSubmit: (draft: PlanDraft) => 'review' | 'adjust' | null;
  /** projected death under the plan in effect (the header's number) */
  currentProjection: number | null;
}) {
  const st = session.state;
  const base = effectivePlan(session);
  const perYear = Math.round(1 / P.substep);
  const yearIdx = Math.floor(st.n / perYear);
  const atUnreviewedBoundary =
    st.n % perYear === 0 && session.reviewedYears === st.n / perYear;
  // At an unreviewed boundary the draft must default to what stepping will
  // ACTUALLY apply — the standing logged intent (driveTo's auto-file re-files
  // lastPlan), not the transient st.studying, which a mid-year skill
  // completion clears. Otherwise the checkbox, the preview's tuition line and
  // the "stepping continues last plan" note would all describe a continuation
  // that stepping does not perform ("the warning is the fair part").
  const effective: PlanDraft = {
    ...base,
    study: atUnreviewedBoundary ? continuationStudy(session) : st.studying,
  };
  const [draft, setDraft] = useState<PlanDraft>(effective);
  const [dirty, setDirty] = useState(false);
  const [filedAs, setFiledAs] = useState<'review' | 'adjust' | null>(null);
  const [refused, setRefused] = useState(false);

  // Import/Wipe swap the session object while this component instance stays
  // mounted: a dirty draft (or stale filed-note) from the OLD life must never
  // survive into the new one. Reset local state when the session identity
  // changes (React's derive-from-props-during-render pattern).
  const [seenSession, setSeenSession] = useState(session);
  if (session !== seenSession) {
    setSeenSession(session);
    setDraft(effective);
    setDirty(false);
    setFiledAs(null);
    setRefused(false);
  }

  // follow the plan in effect until the player starts editing
  useEffect(() => {
    if (!dirty) setDraft(effective);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effective.mode, effective.livingFrac, effective.study, dirty]);

  // a filed-note describes the LAST filing; a freshly opened annual review
  // supersedes it
  useEffect(() => {
    if (atUnreviewedBoundary) {
      setFiledAs(null);
      setRefused(false);
    }
  }, [atUnreviewedBoundary]);
  const wage = P.wages[st.J]!;
  const livingPerYear = draft.livingFrac * wage;
  const preview = previewYear(session, draft, P);
  const dies = preview.dies;
  const deathAge = dies ? settledAge(session, P) + preview.span : null;
  const perYr = (x: number) => (preview.span > 0 ? x / preview.span : 0);
  const flowPerYear = perYr(preview.netFlow);
  // §15.1-2: every control previews its projected-lifespan delta before commit
  const draftProjection = st.alive ? projectDeath(session, P, draft) : null;
  const projDelta =
    draftProjection !== null && currentProjection !== null
      ? draftProjection - currentProjection
      : null;

  const setMode = (mode: Mode) => {
    setDraft((d) => ({ ...d, mode }));
    setDirty(true);
    setFiledAs(null);
  };
  const setLiving = (livingFrac: number) => {
    setDraft((d) => ({ ...d, livingFrac }));
    setDirty(true);
    setFiledAs(null);
  };
  const setStudy = (study: boolean) => {
    setDraft((d) => ({ ...d, study }));
    setDirty(true);
    setFiledAs(null);
  };
  const submit = () => {
    // surface what the filing actually WAS (the session can cross a boundary
    // between render and click; the driver's classification is the truth)
    const askedStudy = !!draft.study;
    const wasStudying = session.state.studying;
    const result = onSubmit(draft);
    setFiledAs(result);
    // a review that asked for study but did not enact it was REFUSED by the
    // engine's gates (canEnroll) — say so; the silently-unticking checkbox
    // must never be the only notice
    setRefused(result === 'review' && askedStudy && !wasStudying && !session.state.studying);
    setDirty(false);
  };

  const sk = skillModel(session, P);
  const resumeCase = sk.next !== null && sk.enrolledSkill === sk.next && sk.progress > 0;
  // the checkbox can be MEANINGFULLY ticked when: already studying (continue),
  // resuming a paid enrolment, or a fresh enrolment the gates allow
  const studyPossible = sk.next !== null && (st.studying || resumeCase || sk.canEnrollNow);
  const promotionPending = sk.K > sk.J;

  return (
    <section className="panel plan" aria-label="Plan">
      <div className="panel-title">
        Year {yearIdx + 1} plan
        {atUnreviewedBoundary ? (
          <span className="plan-status waiting"> · annual review — waiting for you</span>
        ) : (
          <span className="plan-status"> · changes apply mid-year</span>
        )}
      </div>

      {/* toggle buttons, not ARIA radios: all three sit in the Tab order and
          have no arrow-key roving focus, so aria-pressed matches the actual
          keyboard behaviour honestly */}
      <div className="plan-modes" role="group" aria-label="Work mode">
        {MODES.map((m) => (
          <button
            key={m.mode}
            aria-pressed={draft.mode === m.mode}
            className={`plan-mode ${draft.mode === m.mode ? 'selected' : ''}`}
            onClick={() => setMode(m.mode)}
            disabled={!st.alive}
          >
            <span className="plan-mode-name">
              {m.label} <span className="plan-mode-mult">{m.multLabel}</span>
            </span>
            <span className="plan-mode-wage">{money(wage * P.modeMult[m.mode])}/yr</span>
          </button>
        ))}
      </div>

      <div className="plan-living">
        <label htmlFor="living-dial">
          Living <span className="plan-living-hint">(experience spend — the one competing use)</span>
        </label>
        <div className="plan-living-row">
          <input
            id="living-dial"
            type="range"
            min={0}
            max={P.livingFracMax}
            step={0.01}
            value={draft.livingFrac}
            onChange={(e) => setLiving(Number(e.target.value))}
            disabled={!st.alive}
          />
          <span className="plan-living-value">
            {Math.round(draft.livingFrac * 100)}% · {money(livingPerYear)}/yr
          </span>
        </div>
        {st.brokeLock && (
          <div className="broke-note" role="alert">
            BROKE — Living spending suspended at $0 until your balance climbs back above{' '}
            {money(P.debtRelease)}. The filed dial resumes then.
          </div>
        )}
      </div>

      {sk.next !== null && (
        <div className="plan-study">
          {atUnreviewedBoundary ? (
            <>
              <label className="plan-study-row">
                <input
                  type="checkbox"
                  checked={!!draft.study}
                  onChange={(e) => setStudy(e.target.checked)}
                  disabled={!st.alive || (!studyPossible && !draft.study)}
                />
                <span>
                  {st.studying
                    ? `Keep studying Skill ${sk.next}`
                    : resumeCase
                      ? `Resume Skill ${sk.next} (tuition already paid)`
                      : `Study toward Skill ${sk.next} — tuition ${money(sk.tuition!)}, charged at filing`}
                </span>
              </label>
              {!studyPossible && !st.studying && (
                <div className="plan-study-gate">
                  Enrolment gated — see the skill ladder panel for what's missing.
                </div>
              )}
              {st.studying && !draft.study && (
                <div className="plan-study-gate">
                  Pausing lets {sk.progress.toFixed(2)} study-years rust at 25%/yr.
                </div>
              )}
            </>
          ) : (
            <div className="plan-study-gate">
              {st.studying
                ? `Studying Skill ${sk.next} continues — study changes at the annual review.`
                : 'Enrolment happens at the annual review.'}
            </div>
          )}
        </div>
      )}

      {/* the promotion notice must survive the ladder top: at K=6 the study
          block above vanishes (sk.next === null) but the FINAL promotion —
          Master tier, the largest wage jump in the game — still files at this
          review (GDD §3.2 "promotion offers at review") */}
      {atUnreviewedBoundary && promotionPending && (
        <div className="plan-promote">
          Filing this review promotes you to {JOB_TITLES[sk.K] ?? `Tier ${sk.K}`} (
          {money(P.wages[sk.K]!)}/yr).
        </div>
      )}

      <div className="plan-preview">
        {/* the §5 decomposition: WHY the net flow is what it is. Every number
            comes from the previewed engine clone's accumulators (UX-6), and
            income − cost of living − Living − tuition === net flow exactly
            (§6 identity; interest is its own line below; the tuition line
            appears only when the previewed review charges it — the cash set
            piece must reconcile on the year it happens). */}
        <div className="line">
          <span>Income at this plan</span>
          <span>{money(perYr(preview.earned))}/yr</span>
        </div>
        <div className="line">
          <span>− Cost of living (not a choice)</span>
          <span className="neg">{money(-perYr(preview.costOfLiving))}/yr</span>
        </div>
        <div className="line">
          <span>− Living</span>
          <span className={perYr(preview.living) > 0 ? 'neg' : ''}>
            {money(-perYr(preview.living))}/yr
          </span>
        </div>
        {preview.tuition > 0 && (
          <div className="line">
            <span>− Tuition (charged at filing)</span>
            <span className="neg">{money(-perYr(preview.tuition))}/yr</span>
          </div>
        )}
        <div className="line">
          <span>Net flow at this plan</span>
          <span className={flowPerYear < 0 ? 'neg' : ''}>{money(flowPerYear)}/yr</span>
        </div>
        <div className="line">
          <span>End of Year {yearIdx + 1}</span>
          <span className={preview.endS < 0 ? 'neg' : ''}>
            {money(preview.endS)}{' '}
            <span className="plan-preview-int">(incl. {money(preview.interest)} interest)</span>
          </span>
        </div>
        {/* UX-3: the full diff — cash above, morale and the projection here */}
        <div className="line">
          <span>Morale</span>
          <span>
            {st.M.toFixed(0)} now → settles at M* {preview.draftMstar.toFixed(0)}
            <span className="plan-preview-int"> (end of year: {preview.endM.toFixed(0)})</span>
          </span>
        </div>
        {draftProjection !== null && currentProjection !== null && (
          <div className="line">
            <span>Projected lifespan</span>
            <span className={projDelta !== null && projDelta < -0.05 ? 'neg' : ''}>
              {currentProjection.toFixed(1)} → {draftProjection.toFixed(1)}
              {projDelta !== null && Math.abs(projDelta) >= 0.05 && (
                <span className="plan-preview-int">
                  {' '}
                  ({projDelta > 0 ? '+' : '−'}
                  {Math.abs(projDelta).toFixed(1)}y)
                </span>
              )}
            </span>
          </div>
        )}
        {/* §15.1-4b: the blunt truth repeated in the Year Review preview
            whenever the DRAFTED plan's M* sits under the Skill-4 wall — being
            refused at a later review must never be the first notice */}
        {sk.gates.m4 && !sk.gates.m4.ready && preview.draftMstar < P.enrollM && (
          <div className="broke-note" role="alert">
            ⚠ this plan never reaches Skill-4 enrolment morale (settles at M*{' '}
            {preview.draftMstar.toFixed(0)} &lt; {P.enrollM}) — raise Living or rest.
          </div>
        )}
        {preview.brokeAge !== null && (
          <div className="broke-note" role="alert">
            ⚠ this plan crosses the {money(P.debtFloor)} Broke lock at age{' '}
            {preview.brokeAge.toFixed(1)} — Living suspends at $0 until the balance climbs back
            above {money(P.debtRelease)}.
          </div>
        )}
        {dies && deathAge !== null && (
          <div className="plan-death" role="alert">
            † this plan ends the life at age {deathAge.toFixed(1)}
          </div>
        )}
      </div>

      <button className="plan-file" onClick={submit} disabled={!st.alive || !dirty}>
        {atUnreviewedBoundary ? `File Year ${yearIdx + 1} plan` : 'Apply change now'}
      </button>
      {refused && (
        <div className="plan-study-gate" role="alert">
          Enrolment was refused — see the skill ladder for the missing gate. Nothing was
          charged, and stepping will not retry it without a fresh study submission.
        </div>
      )}
      {!dirty && st.alive && (
        <span className="plan-filed-note">
          {filedAs === 'review'
            ? 'Filed as the annual review.'
            : filedAs === 'adjust'
              ? 'Applied as a mid-year change.'
              : atUnreviewedBoundary
                ? effective.study && !st.studying && sk.next !== null && (resumeCase || sk.canEnrollNow)
                  ? `No changes yet — stepping continues last plan and enrols Skill ${sk.next}` +
                    (resumeCase
                      ? ' (tuition already paid).'
                      : ` — tuition ${money(sk.tuition!)} charged then.`)
                  : 'No changes yet — stepping continues last plan.'
                : 'This is the plan in effect.'}
        </span>
      )}
    </section>
  );
}
